import React, { useCallback, useEffect, useState } from "react";

import { Button, Col, Form, Row } from "react-bootstrap";

import { Controller, useForm } from "react-hook-form";
import { useTypedParams } from "react-router-typesafe-routes/dom";
import { ROUTES } from "src/routes";

import { BankDetailsFragment, BankType, ParticipantTypes, useUpdateBankDetailsMutation } from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { ToastMessage } from "@components/ToastMessage";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { FieldFormatter } from "@components/detail_drawer/FieldFormatter";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import { DetailCell } from "@components/resale_submission/DetailCell";
import { useAuthenticationContext } from "@context/authentication";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { OverseasBankDetails } from "@pages/register/steps/UserDetails/OverseasBankDetails";
import { Role } from "@roles";

import { bankDetailsErrorResolver } from "./bankDetailsErrorResolver";

import styles from "./BankDetailsAccordion.module.scss";

interface BankDetailsProps {
  bankDetails: BankDetailsFragment;
  onBankDetailsTypeChange: (type: BankType) => void;
  isEditbankDetails: boolean;
  setIsEditbankDetails: (type: boolean) => void;
  onUpdate: () => void;
  participantTypes: (ParticipantTypes | undefined)[];
}

const BANK_DETAILS_FORMATTERS: FieldFormatter<BankDetailsFragment>[] = [
  {
    title: "Overseas bank account name",
    format: (bankDetails: BankDetailsFragment) => bankDetails.account_name || "-",
  },
  {
    title: "Overseas bank SWIFT BIC code",
    format: (bankDetails: BankDetailsFragment) => bankDetails.swift || "-",
  },
  {
    title: "Overseas account IBAN number",
    format: (bankDetails: BankDetailsFragment) => bankDetails.iban || "-",
  },
  { title: "Overseas country", format: (bankDetails: BankDetailsFragment) => bankDetails.branch_address || "-" },
  {
    title: "Overseas intermediary bank name",
    format: (bankDetails: BankDetailsFragment) => bankDetails.intermediary_bank_name || "-",
  },
  {
    title: "Overseas intermediary bank SWIFT code",
    format: (bankDetails: BankDetailsFragment) => bankDetails.intermediary_swift || "-",
  },
  {
    title: "Overseas intermediary bank country",
    format: (bankDetails: BankDetailsFragment) => bankDetails.intermediary_country || "-",
  },
];

export const OverseasBankDetailsAccordion: React.FC<BankDetailsProps> = ({
  bankDetails,
  onBankDetailsTypeChange,
  isEditbankDetails,
  setIsEditbankDetails,
  onUpdate,
  participantTypes,
}) => {
  const { auth } = useAuthenticationContext();
  const [bankInfo, setBankInfo] = useState(bankDetails);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [updateBankDetailsMutation] = useUpdateBankDetailsMutation();
  const { participantId } = useTypedParams(ROUTES.Profile.AccountDetails);
  const [isBranchAddressRequired, setIsBranchAddressRequired] = useState(false);

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
  } = useForm<UserDetailsInput>({
    mode: "onChange",
    reValidateMode: "onChange",

    resolver: (data) => {
      return bankDetailsErrorResolver(
        data,
        BankType.OVERSEAS,
        participantTypes?.filter((type): type is ParticipantTypes => type !== undefined).map((type) => type.toString()),
      );
    },
  });

  useEffect(() => {
    reset({
      bank_details: { ...bankInfo, bank_type: BankType.OVERSEAS },
    });
  }, [bankInfo, reset]);

  useEffect(() => {
    const bankDetails = getValues("bank_details");
    const hasOverseasFieldsFilled = !!(
      bankDetails.account_name ||
      bankDetails.swift ||
      bankDetails.iban ||
      !bankDetails.branch_address
    );

    setIsBranchAddressRequired(hasOverseasFieldsFilled);
  }, [getValues]);

  const handleEditToggle = () => {
    setIsEditbankDetails(!isEditbankDetails);
  };

  const submitHandler = useCallback(async () => {
    const formData = getValues();
    try {
      const {
        account_name,
        swift,
        iban,
        branch_address,
        intermediary_bank_name,
        intermediary_swift,
        intermediary_country,
      } = formData.bank_details;
      const allFieldsEmpty =
        !account_name &&
        !swift &&
        !iban &&
        !branch_address &&
        !intermediary_bank_name &&
        !intermediary_swift &&
        !intermediary_country;

      if (allFieldsEmpty) {
        setIsEditbankDetails(false);
        return;
      }

      const response = await updateBankDetailsMutation({
        variables: {
          input: {
            account_name: formData.bank_details.account_name,
            swift: formData.bank_details.swift,
            iban: formData.bank_details.iban,
            branch_address: formData.bank_details.branch_address,
            intermediary_bank_name: formData.bank_details.intermediary_bank_name,
            intermediary_swift: formData.bank_details.intermediary_swift,
            intermediary_country: formData.bank_details.intermediary_country,
            bank_type: BankType.OVERSEAS,
            participant_id: participantId ?? -1,
          },
        },
      });
      if (response.data?.updateBankDetails) {
        setIsEditbankDetails(false);
        setBankInfo(response.data.updateBankDetails);
        onUpdate();
        setToastMessage("Bank details updated successfully!");
      } else {
        setErrorMessage("Failed to update bank details");
        setToastMessage("Failed to update bank details");
      }
    } catch (error) {
      setErrorMessage(`An error occurred while updating bank details: ${error}`);
      setToastMessage("An error occurred while updating bank details");
    } finally {
      setShowToast(true);
    }
  }, [getValues, onUpdate, participantId, setIsEditbankDetails, updateBankDetailsMutation]);

  return (
    <>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type="info" />
      <CustomAccordion
        accordionKey={"OverseasBankDetails"}
        contrastOpenStyle={false}
        header={<h5 className="mb-0">Overseas bank details</h5>}
        defaultActiveKey={"OverseasBankDetails"}
        customHeaderStyle="py-4"
        showBottomLine
      >
        <Row onClick={(e) => e.stopPropagation()}>
          {isEditbankDetails ? (
            <>
              <Row>
                <Col md={12} sm={12}>
                  <Form.Group>
                    <Form.Label>Is your bank domestic or overseas?</Form.Label>
                    <Row>
                      <Col>
                        <Controller
                          name="bank_details.bank_type"
                          control={control}
                          render={({ field }) => (
                            <>
                              <Form.Check
                                className={styles["form-check"]}
                                type="radio"
                                label="Domestic"
                                value="DOMESTIC"
                                checked={field.value === "DOMESTIC"}
                                onChange={() => {
                                  field.onChange("DOMESTIC");
                                  onBankDetailsTypeChange(BankType.DOMESTIC);
                                }}
                              />
                            </>
                          )}
                        />
                      </Col>
                      <Col>
                        <Controller
                          name="bank_details.bank_type"
                          control={control}
                          render={({ field }) => (
                            <>
                              <Form.Check
                                className={styles["form-check"]}
                                aria-selected="true"
                                type="radio"
                                label="Overseas"
                                value="OVERSEAS"
                                checked={field.value === "OVERSEAS"}
                                onChange={() => {
                                  field.onChange("OVERSEAS");
                                  onBankDetailsTypeChange(BankType.OVERSEAS);
                                }}
                              />
                            </>
                          )}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
              </Row>
              <OverseasBankDetails
                control={control}
                errors={errors}
                isBranchAddressRequired={isBranchAddressRequired}
              />
              <div className="mt-3">
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                <Col className="d-flex justify-content-end">
                  <Button variant="secondary" onClick={() => setIsEditbankDetails(false)} className="me-2">
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleSubmit(submitHandler)}>
                    Save
                  </Button>
                </Col>
              </div>
            </>
          ) : (
            <Row>
              <Col md={11}>
                <Row className="gap-4">
                  {BANK_DETAILS_FORMATTERS.map(({ title, format }) => (
                    <Col md={5} className="d-flex flex-column" key={title}>
                      <DetailCell title={title} value={format(bankInfo)} />
                    </Col>
                  ))}
                </Row>
              </Col>
              {!isEditbankDetails && (
                <Col md={1} className="d-flex justify-content-end">
                  <ProtectedComponent requiredGroups={[Role.MemberServices, Role.RoyaltyRecipient]} auth={auth}>
                    <IconLink to="#" src={PenWriteEditIcon} text="Edit" className="me-2" onClick={handleEditToggle} />
                  </ProtectedComponent>
                </Col>
              )}
            </Row>
          )}
        </Row>
      </CustomAccordion>
    </>
  );
};
